import { isEqual, isPlainObject } from 'lodash'

/**
 * Deep-diffs `current` against `baseline` and returns an object containing
 * only the keys that were added or changed, recursively (a key whose value
 * is unchanged from `baseline` - at any depth - is omitted).
 *
 * Why: the client used to always send the *entire* `submission_values` tree
 * on every autosave. The backend used to merge that in with a shallow
 * `{...original, ...submission}`, so any top-level key present in the
 * payload (e.g. `inspection_items`) fully replaced what was stored - even
 * keys the client never touched. Two sessions/devices editing the same
 * report concurrently could silently wipe out each other's answers this way
 * (see EYES-2764). The backend now deep-merges instead of replacing, and
 * this function is the client-side half of the fix: sending only what
 * actually changed shrinks the blast radius of any payload to just the
 * fields this session edited, so a stale/partial local snapshot can no
 * longer clobber answers saved elsewhere in the meantime.
 *
 * Plain objects are recursed into; arrays, dates and any other value are
 * compared as a whole with lodash `isEqual` and, when different, included
 * wholesale in the diff - there's no stable per-item identity to diff a
 * list by (e.g. a `tracking_items` category, which is an array of entries).
 *
 * Note: this can only express "added or changed" keys, not "a key was
 * removed" (it only walks `current`'s keys). That matches how this form
 * edits answers today - clearing an answer sets it to an empty/falsy value
 * rather than deleting the key - so it's not a limitation in practice here.
 */
export function diffSubmissionValues<T extends object>(
  baseline: T | null | undefined,
  current: T | null | undefined,
): Partial<T> {
  if (!current) return {}
  if (!baseline) return current

  const diff: Record<string, unknown> = {}
  const currentRecord = current as Record<string, unknown>
  const baselineRecord = baseline as Record<string, unknown>

  for (const key of Object.keys(currentRecord)) {
    const currentValue = currentRecord[key]
    const baselineValue = baselineRecord[key]

    if (isPlainObject(currentValue) && isPlainObject(baselineValue)) {
      const nestedDiff = diffSubmissionValues(
        baselineValue as Record<string, unknown>,
        currentValue as Record<string, unknown>,
      )
      if (Object.keys(nestedDiff).length > 0) {
        diff[key] = nestedDiff
      }
      continue
    }

    if (!isEqual(currentValue, baselineValue)) {
      diff[key] = currentValue
    }
  }

  return diff as Partial<T>
}
